import React, { useState } from "react";
import {
  Container,
  Card,
  CardMedia,
  Typography,
  Box,
  Stack,
  ThemeProvider,
  Alert,
  AlertTitle,
} from "@mui/material";
import moldex from "./moldex.png";
import forming from "./forming.png";
import { createTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { APP_ID } from "../utils/constants";
import useEventSettings from "../hooks/useEventSettings";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

// Create a modern theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#6366F1",
    },
    secondary: {
      main: "#10B981",
    },
    background: {
      default: "#F9FAFB",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h6: { fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
});

const ModernSelectionComponent = () => {
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState(null);
  const { data, isLoading, isError, error } = useEventSettings(APP_ID);

  // Sample images

  const handleCardSelect = (cardId) => {
    if (cardId === 1) {
      navigate(`/visitor-registration/imtex`);
    } else if (cardId === 2) {
      navigate(`/visitor-registration/moldex-fastnex`);
    }
    setSelectedCard(cardId);
  };
  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert>{getErrorResponse(error)}</Alert>;
  }
  return (
    <Alert severity="warning">
      <AlertTitle>IMTEX 2026</AlertTitle>
      The registration window for IMTEX 2026 has been closed. For further
      information, please contact the event organizers.
    </Alert>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F9FAFB 0%, #E5E7EB 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header image */}
        <Box
          component="img"
          src={data?.selectorBanner}
          alt="Header"
          sx={{
            width: "100%",
            maxWidth: "1200px", // ✅ prevents overflow on big screens
            height: "auto",
            display: "block",
            mx: "auto", // ✅ center horizontally
            px: 2,
            borderRadius: 2,
            mb: 4,
          }}
        />
        <Container maxWidth="md" sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              mb: 4,
              fontWeight: 700,
              background: "linear-gradient(135deg, #6366F1 0%, #10B981 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            I am Attending...
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="center"
            alignItems="center"
            spacing={4}
            sx={{ mb: 6 }}
          >
            {/* Card 1 */}
            <Card
              variant="outlined"
              sx={{
                width: { xs: "100%", sm: 280, md: 300 },
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: selectedCard === 1 ? "3px solid" : "1px solid",
                borderColor: selectedCard === 1 ? "primary.main" : "grey.300",
                boxShadow:
                  selectedCard === 1
                    ? "0 6px 20px rgba(99,102,241,0.2)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: "0 6px 20px rgba(99,102,241,0.2)",
                },
              }}
              onClick={() => handleCardSelect(1)}
            >
              <CardMedia
                component="img"
                height="240"
                image={data?.imtexSelectorLogo ?? ""}
                alt="Option 1"
                sx={{
                  objectFit: "contain",
                  p: 1,
                  backgroundColor: "white",
                }}
              />
              <Box sx={{ pb: 2, textAlign: "center" }}>
                <Typography fontWeight="bold" color="text.secondary">
                  Click 👆 to proceed
                </Typography>
              </Box>
            </Card>

            {/* Card 2 */}
            <Card
              variant="outlined"
              sx={{
                width: { xs: "100%", sm: 280, md: 300 },
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: selectedCard === 2 ? "3px solid" : "1px solid",
                borderColor: selectedCard === 2 ? "primary.main" : "grey.300",
                boxShadow:
                  selectedCard === 2
                    ? "0 6px 20px rgba(99,102,241,0.2)"
                    : "0 2px 8px rgba(0,0,0,0.05)",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: "0 6px 20px rgba(99,102,241,0.2)",
                },
              }}
              onClick={() => handleCardSelect(2)}
            >
              <CardMedia
                component="img"
                height="240"
                image={data?.moldexSelectorLogo ?? ""}
                alt="Option 2"
                sx={{
                  objectFit: "contain",
                  p: 1,
                  backgroundColor: "white",
                }}
              />
              <Box sx={{ pb: 2, textAlign: "center" }}>
                <Typography fontWeight="bold" color="text.secondary">
                  Click 👆 to proceed
                </Typography>
              </Box>
            </Card>
          </Stack>
        </Container>

        {/* Footer image */}
        <Box
          component="img"
          src={data?.selectorFooter}
          alt="Footer"
          sx={{
            width: "100%",
            maxWidth: "1200px", // ✅ consistent max width
            height: "auto",
            display: "block",
            mx: "auto",
            px: 2,
            borderRadius: 2,
            mt: "auto",
            mb: 2,
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ModernSelectionComponent;
